import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { WorkspaceSummary } from "../api/types";
import { ChevronDown, LayoutGrid } from "../compose/Icon";
import { resolveAccent } from "../lib/accents";
import { useTheme } from "../theme/ThemeProvider";

/**
 * Workspace switcher chip (design shell §1.1). Shows the current workspace with
 * its accent swatch; the popover lists every membership (each previewing its
 * own accent) plus "All workspaces". Switching is a perceptible change of room.
 */
export function WorkspaceSwitcher({
  current,
  workspaces,
}: {
  current: WorkspaceSummary | null;
  workspaces: WorkspaceSummary[];
}) {
  const navigate = useNavigate();
  const { resolved } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const swatch = (accent: string) => {
    const a = resolveAccent(accent);
    return resolved === "dark" ? a.dark : a.light;
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        className="switcher"
        data-testid="workspace-switcher"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span
          className="switcher__swatch"
          style={{ background: swatch(current?.accent ?? "cyanotype") }}
          aria-hidden
        />
        <span style={{ minWidth: 0 }}>
          <span className="switcher__name" style={{ display: "block" }}>
            {current?.name ?? "elaborate"}
          </span>
          <span className="switcher__client" style={{ display: "block" }}>
            {current?.client ?? "Choose a workspace"}
          </span>
        </span>
        <ChevronDown className="switcher__caret" size={16} strokeWidth={1.5} />
      </button>

      {open ? (
        <div
          className="popover"
          role="menu"
          style={{ top: "calc(100% - 2px)", bottom: "auto" }}
        >
          {workspaces.map((w) => (
            <button
              key={w.slug}
              type="button"
              className="popover__item"
              role="menuitem"
              data-testid={`switch-${w.slug}`}
              onClick={() => {
                setOpen(false);
                navigate(`/w/${w.slug}`);
              }}
            >
              <span
                className="switcher__swatch"
                style={{ width: 16, height: 16, background: swatch(w.accent) }}
                aria-hidden
              />
              {w.name}
            </button>
          ))}
          <button
            type="button"
            className="popover__item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate("/");
            }}
          >
            <LayoutGrid size={16} strokeWidth={1.5} />
            All workspaces
          </button>
        </div>
      ) : null}
    </div>
  );
}
